import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuthStore } from './store/authStore';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import RoleRoute from './components/RoleRoute';
import SystemAccessRoute from './components/SystemAccessRoute';
import Login from './pages/Login';
import SystemSelection from './pages/SystemSelection';
import Settings from './pages/Settings';
import Dashboard from './pages/Dashboard';
import SpareParts from './pages/SpareParts';
import QRScanner from './pages/QRScanner';
import Transactions from './pages/Transactions';
import Machines from './pages/Machines';
import Categories from './pages/Categories';
import Suppliers from './pages/Suppliers';
import Maintenance from './pages/Maintenance';
import Users from './pages/Users';
import PRManagement from './pages/PRManagement';
import PRDashboard from './pages/PRDashboard';
import PrintingProcessManager from './pages/PrintingProcessManager';
import PrintingManagerSelection from './pages/PrintingManagerSelection';
import MachineEfficiency from './pages/MachineEfficiency';
import OEEDashboard from './pages/OEEDashboard';
import PfpTrollyManagement from './pages/PfpTrollyManagement';
import MaintenanceManagement from './pages/MaintenanceManagement';
import QualityManagement from './pages/QualityManagement';
import IoTDevicesManagement from './pages/IoTDevicesManagement';
import FabricWidthDashboard from './pages/FabricWidthDashboard';
import EnergyManagementSystem from './pages/EnergyManagementSystem';
import WasteManagementSystem from './pages/WasteManagementSystem';
import CutWasteManagement from './pages/CutWasteManagement';
import ReprocessingManagement from './pages/ReprocessingManagement';
import DyeStockManagement from './pages/DyeStockManagement';

const theme = createTheme({
  palette: {
    primary: {
      main: '#00695C',
    },
    secondary: {
      main: '#004d40',
    },
  },
});

function App() {
  const initAuth = useAuthStore((state) => state.initAuth);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<PrivateRoute><SystemSelection /></PrivateRoute>} />

          {/* Settings (all authenticated users) */}
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Spare Parts System */}
          <Route
            path="/spare-parts-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="spare-parts" allowedRoles={['admin', 'manager', 'technician', 'viewer']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/spare-parts" element={<SpareParts />} />
                      <Route path="/qr-scanner" element={<QRScanner />} />
                      <Route path="/transactions" element={<Transactions />} />
                      <Route path="/machines" element={<RoleRoute allowedRoles={['admin', 'manager']}><Machines /></RoleRoute>} />
                      <Route path="/categories" element={<RoleRoute allowedRoles={['admin', 'manager']}><Categories /></RoleRoute>} />
                      <Route path="/suppliers" element={<RoleRoute allowedRoles={['admin', 'manager']}><Suppliers /></RoleRoute>} />
                      <Route path="/maintenance" element={<Maintenance />} />
                      <Route path="*" element={<Navigate to="/spare-parts-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* PR Management System */}
          <Route
            path="/pr-management-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="pr-management" allowedRoles={['admin', 'manager', 'technician', 'viewer']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<PRDashboard />} />
                      <Route path="/purchase-requisitions" element={<PRManagement />} />
                      <Route path="*" element={<Navigate to="/pr-management-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Printing Process System */}
          <Route
            path="/printing-process-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="printing-process" allowedRoles={['admin']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<PrintingManagerSelection />} />
                      <Route path="/soff" element={<PrintingProcessManager />} />
                      <Route path="/bulk" element={<PrintingProcessManager />} />
                      <Route path="/cad" element={<PrintingProcessManager />} />
                      <Route path="*" element={<Navigate to="/printing-process-system" replace />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Machine Efficiency System */}
          <Route
            path="/machine-efficiency-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="machine-efficiency" allowedRoles={['admin', 'manager', 'technician', 'machine']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<MachineEfficiency />} />
                      <Route path="/oee-dashboard" element={<OEEDashboard />} />
                      <Route path="*" element={<Navigate to="/machine-efficiency-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* PFP Trolly System */}
          <Route
            path="/pfp-trolly-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="pfp-trolly" allowedRoles={['admin', 'manager', 'technician']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<PfpTrollyManagement />} />
                      <Route path="*" element={<Navigate to="/pfp-trolly-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Maintenance Management System */}
          <Route
            path="/maintenance-management-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="maintenance-management" allowedRoles={['admin', 'manager', 'technician', 'viewer']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<MaintenanceManagement />} />
                      <Route path="/preventive" element={<MaintenanceManagement maintenanceView="preventive" />} />
                      <Route path="/predictive" element={<MaintenanceManagement maintenanceView="predictive" />} />
                      <Route path="*" element={<Navigate to="/maintenance-management-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Quality Management System */}
          <Route
            path="/quality-management-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="quality-management" allowedRoles={['admin', 'manager', 'technician']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<QualityManagement />} />
                      <Route path="*" element={<Navigate to="/quality-management-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* IoT / Wireless Sensor Network System */}
          <Route
            path="/iot-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="iot-system" allowedRoles={['admin', 'manager', 'technician']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<IoTDevicesManagement />} />
                      <Route path="/fabric-width" element={<FabricWidthDashboard />} />
                      <Route path="*" element={<Navigate to="/iot-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Energy Management System */}
          <Route
            path="/energy-management-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="energy-management" allowedRoles={['admin', 'manager']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<EnergyManagementSystem />} />
                      <Route path="/flowvision" element={<EnergyManagementSystem />} />
                      <Route path="/data-entry" element={<EnergyManagementSystem />} />
                      <Route path="/meter-readings" element={<EnergyManagementSystem />} />
                      <Route path="/analytics" element={<EnergyManagementSystem />} />
                      <Route path="*" element={<Navigate to="/energy-management-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Reprocessing Management System */}
          <Route
            path="/reprocessing-management-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="reprocessing-management" allowedRoles={['admin', 'manager', 'technician', 'viewer']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<ReprocessingManagement />} />
                      <Route path="*" element={<Navigate to="/reprocessing-management-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Dye Stock Management System */}
          <Route
            path="/dye-stock-management-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="dye-stock-management" allowedRoles={['admin', 'manager', 'technician', 'viewer']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<DyeStockManagement />} />
                      <Route path="*" element={<Navigate to="/dye-stock-management-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          {/* Waste Management System */}
          <Route
            path="/waste-management-system/*"
            element={
              <PrivateRoute>
                <SystemAccessRoute systemId="waste-management" allowedRoles={['admin', 'manager', 'technician', 'viewer']}>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<WasteManagementSystem />} />
                      <Route path="/cut-waste" element={<CutWasteManagement />} />
                      <Route path="*" element={<Navigate to="/waste-management-system" />} />
                    </Routes>
                  </Layout>
                </SystemAccessRoute>
              </PrivateRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
